/**
 * Calculator Eloqua Modal Form
 *
 * Designed to live inside a `modal-container` <dialog>. The Submit Button
 * opens the host dialog and calls `setController(controller, dialog)` on
 * this instance so it can build the calculator results payload at submit
 * time. On a successful submission the host dialog is closed and the
 * calculator is asked to swap into its results view.
 *
 * Field model
 * ───────────
 * Every visible control is author-driven from a single `formFields`
 * multifield in the dialog. Each row carries:
 *   • `data-elq-field-type` — `text` | `email` | `country` | `state` |
 *                             `dropdown` (set on the field WRAPPER)
 *   • `name`                — Eloqua column it submits as (authored)
 *   • `data-elq-required`   — `'true'` if required (authored)
 *   • `data-elq-type`       — `text` | `email` | `select` (drives validators)
 *
 * The `elq-` prefix is required so the calculator container's `validate()`,
 * which scans `[data-required="true"]` across the whole `.calc-container`,
 * doesn't see still-empty modal inputs as missing calculator answers and
 * silently block the Submit Button.
 *
 * Country → State wiring
 * ──────────────────────
 * Country options are author-supplied: the row's authored Value goes on
 * the wire (e.g. `US`), and the row's authored Label is what the
 * shared `countryToStatesMap` is keyed by — so authors should type the
 * country's display name (e.g. `United States`) as the option label even
 * if they pick a different value. When the country select changes, this
 * module reads the SELECTED OPTION's textContent, looks it up in
 * `countryToStatesMap`, and either:
 *   • repopulates the State select with that country's sub-regions and
 *     reveals it, or
 *   • hides the State select and clears its value (the country has no
 *     sub-region picklist).
 *
 * Hidden state selects are also `disabled` so `FormData` skips them at
 * submit time — Eloqua never sees an empty `$SubRegion` when the form
 * is for a country without sub-regions.
 *
 * Submission contract
 * ───────────────────
 * The body POSTed to Eloqua merges:
 *   • Every input/select in the <form> (visible + hidden + author-supplied
 *     hidden extras) via `FormData`.
 *   • Each calculator input/slider that has an "Eloqua Field Name" set
 *     on its dialog gets one entry in the body keyed by that field
 *     name. The value is `Question - Answer` if the
 *     "Include Question and Answer in Submission" checkbox is checked,
 *     otherwise just the answer value.
 *   • The shareable results URL (`<page>#<lz-payload>`) under the
 *     authored "Shareable Results URL — Eloqua Field Name", when set.
 *   • Any UTM params present on the current URL.
 *
 * Eloqua endpoint, form names, site id, campaign id, confirmation email
 * id are all author fields rendered as hidden inputs in the HTL — the
 * JS only reads `data-elq-endpoint` (the URL to POST to) and
 * `data-results-url-field` (the optional Eloqua column for the results URL).
 */
import { Component, registerComponent } from 'js/component.js';
import { countryCodeToName, countryToStatesMap, euCountries } from 'js/constants.js';

const COMPONENT = 'calc-eloqua-modal';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Country-conditional logic (state field, CASL, GDPR) is anchored on the
   option's VALUE — the authored ISO alpha-2 code, which is stable across
   language masters — not the visible label, which gets translated. The
   value is resolved to a canonical English country name via
   `countryCodeToName`, and that name is used to key into the existing
   `countryToStatesMap` and `euCountries` lists (kept in English so the
   data structures can stay shared with software-license-form and
   abm-intent-drawer). */

/* Canonical name that triggers the CASL consent checkbox. The country
   value `CA` resolves to this name via `countryCodeToName`. */
const CASL_COUNTRY = 'Canada';

/* `euCountries` from `webpack/util/js/constants.js` is the canonical
   GDPR list shared with software-license-form / abm-intent-drawer. We
   build a Set once for O(1) lookups inside the country change handler. */
const GDPR_COUNTRY_SET = new Set(euCountries);

/* The Eloqua "form URL" hidden input, stamped at init time with the
   current page URL (origin + pathname). Eloqua uses this to attribute
   the submission to the page that hosted the form. */
const FORM_URL_INPUT_NAME = '$FormURL';

const UTM_KEYS = [
    'utm_campaign',
    'utm_creative',
    'utm_medium',
    'utm_placement',
    'utm_source',
    'utm_content',
    'utm_term',
];
const FUTPRINT_CALCULATOR_ENDPOINT = '/bin/rockwell-automation/futprint/calculator-submit';

/* Localized "please select" string used as the leading entry of the
   State select when it's repopulated. */
const STATE_PLACEHOLDER_LABEL = 'Please select';

export class CalculatorEloquaModal extends Component {

    init() {
        this.element.calcEloquaModal = this;
        this.controller = null;
        this.hostDialog = null;

        /* In WCM edit mode the HTL renders the consent rows visible
           (without `hidden` / `disabled`) so the author can see the
           consent configuration. Skip the runtime hide/show wiring in
           that mode — the country select isn't being interacted with by
           an actual visitor. */
        this.isEditMode = this.element.getAttribute('data-edit-mode') === 'true';

        this.dom = {
            form: this.element.querySelector(`.${COMPONENT}__form`),
            submit: this.element.querySelector(`.${COMPONENT}__submit`),
            formError: this.element.querySelector(`.${COMPONENT}__form-error`),
            formUrlInput: this.element.querySelector(`input[name="${FORM_URL_INPUT_NAME}"]`),
            /* Every visible form control: text inputs + selects + the
               two consent checkboxes. Order is the DOM order. Snapshotted
               once at init — fields don't appear or disappear at runtime
               (the State / CASL / GDPR wrappers are just shown/hidden). */
            fields: Array.from(this.element.querySelectorAll(
                `.${COMPONENT}__input, .${COMPONENT}__select, .${COMPONENT}__checkbox`,
            )),
        };

        this.config = {
            elqEndpoint: this.element.getAttribute('data-elq-endpoint') || '',
            resultsUrlField: this.element.getAttribute('data-results-url-field') || '',
            errorRequired: this.element.getAttribute('data-error-required') || 'This field is required.',
            errorEmail: this.element.getAttribute('data-error-email') || 'Please enter a valid email.',
            errorConsent: this.element.getAttribute('data-error-consent') || 'Please confirm to continue.',
            errorNotConfigured: this.element.getAttribute('data-error-not-configured') || 'Form is not configured. Please contact support.',
            errorSubmission: this.element.getAttribute('data-error-submission') || 'Something went wrong. Please try again.',
            /* Futprint dashboard payload reads four form fields by their
               authored Eloqua Field Name and forwards them under stable
               snake_case keys. Defaults match the legacy Rockwell Eloqua
               column names ($Country / $Industry / $JobFunction /
               $JobLevel) so most calculator instances need no extra
               authoring; the Futprint Key Mapping dialog tab overrides
               them per-instance when an Eloqua form uses different names. */
            futprintKeys: {
                country: this.element.getAttribute('data-futprint-country-key') || '$Country',
                industry: this.element.getAttribute('data-futprint-industry-key') || '$Industry',
                job_function: this.element.getAttribute('data-futprint-job-function-key') || '$JobFunction',
                job_level: this.element.getAttribute('data-futprint-job-level-key') || '$JobLevel',
            },
        };

        if (this.dom.formUrlInput) {
            this.dom.formUrlInput.value = window.location.origin + window.location.pathname;
        }

        if (!this.isEditMode) {
            this.hideStateField();
            this.hideConsent('casl');
            this.hideConsent('gdpr');
            this.hideDisclaimer();
            /* Drive consent + disclaimer + state from the country
               select's CURRENT value (defaults to the empty
               placeholder, which matches nothing — all three stay
               hidden). Doing this on init covers browsers that
               auto-fill the select from cached values after a
               back-navigation. */
            this.syncStateForCountry();
        }
        this.applyInitialFilledState();
        this.addEventListeners();
    }

    /* ───── Country → State wiring ───── */

    /** First country wrapper (the special `country` field type). */
    getCountrySelect() {
        const wrapper = this.element.querySelector('[data-elq-field-type="country"]');
        return wrapper ? wrapper.querySelector(`.${COMPONENT}__select`) : null;
    }

    /** First state wrapper (the special `state` field type). */
    getStateWrapper() {
        return this.element.querySelector('[data-elq-field-type="state"]');
    }

    getStateSelect() {
        const wrapper = this.getStateWrapper();
        return wrapper ? wrapper.querySelector(`.${COMPONENT}__select`) : null;
    }

    /**
     * Repopulate the State select based on the country currently selected.
     *
     * Lookup is anchored on the selected option's VALUE (the authored ISO
     * alpha-2 code — stable across language masters), resolved via
     * `countryCodeToName` to a canonical English country name that keys
     * into the shared `countryToStatesMap`. Reading the option's visible
     * text would break the lookup the moment the page is translated.
     *
     * If the country has no entry in the map (most countries don't), the
     * State field is hidden and disabled so `FormData` skips it on submit.
     * Falls back to using the raw value as the lookup key when it isn't
     * a recognized code (covers authors who put full English names on
     * the value attribute instead of alpha-2 codes).
     */
    syncStateForCountry() {
        // In edit mode the modal renders inline as a preview — the
        // author isn't an actual visitor, so no runtime sync. The HTL
        // already rendered the state row hidden and the consent rows
        // visible (regardless of country) for verification.
        if (this.isEditMode) return;

        const countrySelect = this.getCountrySelect();
        const selectedOption = countrySelect
            ? countrySelect.options[countrySelect.selectedIndex]
            : null;
        const countryValue = selectedOption ? (selectedOption.value || '').trim() : '';
        const countryName = countryValue ? (countryCodeToName[countryValue] || countryValue) : '';

        // Consent checkboxes are independent of the State select (a
        // visitor in a non-state country can still need a CASL/GDPR
        // box), so drive them from every country change regardless of
        // whether the state lookup yields anything.
        this.syncConsentForCountry(countryName);

        const stateWrapper = this.getStateWrapper();
        const stateSelect = this.getStateSelect();
        if (!countrySelect || !stateWrapper || !stateSelect) return;

        const states = countryName ? countryToStatesMap[countryName] : null;

        if (!states || !states.length) {
            this.hideStateField();
            return;
        }

        // Wipe existing options, keep a leading empty placeholder so the
        // floating label can sit centered until a state is picked.
        stateSelect.innerHTML = '';
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.hidden = true;
        placeholder.selected = true;
        placeholder.textContent = '';
        stateSelect.appendChild(placeholder);

        // Author-friendly second placeholder visible inside the open
        // dropdown — picked up by `STATE_PLACEHOLDER_LABEL` so the empty
        // initial state isn't a mystery.
        const visibleHint = document.createElement('option');
        visibleHint.value = '';
        visibleHint.disabled = true;
        visibleHint.textContent = STATE_PLACEHOLDER_LABEL;
        stateSelect.appendChild(visibleHint);

        states.forEach(({ code, name }) => {
            const opt = document.createElement('option');
            opt.value = code;
            opt.textContent = name;
            stateSelect.appendChild(opt);
        });

        this.showStateField();
    }

    hideStateField() {
        const wrapper = this.getStateWrapper();
        const stateSelect = this.getStateSelect();
        if (!wrapper) return;
        wrapper.hidden = true;
        if (stateSelect) {
            stateSelect.value = '';
            stateSelect.disabled = true;
        }
        this.clearFieldError(stateSelect);
        wrapper.classList.remove(`${COMPONENT}__field--filled`);
    }

    showStateField() {
        const wrapper = this.getStateWrapper();
        const stateSelect = this.getStateSelect();
        if (!wrapper) return;
        wrapper.hidden = false;
        if (stateSelect) {
            stateSelect.disabled = false;
        }
        this.syncFilledState(stateSelect);
    }

    /* ───── Consent (CASL / GDPR) ───── */

    /**
     * Look up the consent wrapper for the given kind, by its
     * `data-elq-field-type` marker on the row's outer wrapper.
     */
    getConsentWrapper(kind) {
        return this.element.querySelector(`[data-elq-field-type="${kind}"]`);
    }

    getConsentCheckbox(kind) {
        const wrapper = this.getConsentWrapper(kind);
        return wrapper ? wrapper.querySelector(`.${COMPONENT}__checkbox`) : null;
    }

    /**
     * Show CASL when the visitor picks Canada, GDPR when the visitor
     * picks an EU/EEA country (per the shared `euCountries` list), hide
     * both otherwise. The authored disclaimer richtext follows the same
     * rule — it's consent copy that only applies to those same regions,
     * so it's revealed iff either consent checkbox is active and stays
     * hidden for every other country (including visitors who skip the
     * country select entirely). `countryName` is the canonical English
     * name already resolved upstream from the option's VALUE — so this
     * lookup keeps working after the page is translated (the option
     * labels change, the option values don't).
     */
    syncConsentForCountry(countryName) {
        const showCasl = !!countryName && countryName === CASL_COUNTRY;
        const showGdpr = !!countryName && GDPR_COUNTRY_SET.has(countryName);
        if (showCasl) {
            this.showConsent('casl');
        } else {
            this.hideConsent('casl');
        }
        if (showGdpr) {
            this.showConsent('gdpr');
        } else {
            this.hideConsent('gdpr');
        }
        if (showCasl || showGdpr) {
            this.showDisclaimer();
        } else {
            this.hideDisclaimer();
        }
    }

    showConsent(kind) {
        const wrapper = this.getConsentWrapper(kind);
        const checkbox = this.getConsentCheckbox(kind);
        if (!wrapper || !checkbox) return;
        wrapper.hidden = false;
        checkbox.disabled = false;
    }

    /**
     * Hide a consent row, clear the check, and disable the input so
     * `FormData` skips it on submit (Eloqua never receives a
     * `$MarketingOptIn=` for visitors in non-applicable regions).
     */
    hideConsent(kind) {
        const wrapper = this.getConsentWrapper(kind);
        const checkbox = this.getConsentCheckbox(kind);
        if (!wrapper) return;
        wrapper.hidden = true;
        if (checkbox) {
            checkbox.checked = false;
            checkbox.disabled = true;
            this.clearFieldError(checkbox);
        }
    }

    /* ───── Country-conditional disclaimer ───── */

    /**
     * Disclaimer is the consent / data-handling richtext that sits
     * between the consent checkboxes and the Submit button. It rides
     * with CASL/GDPR — visible iff either consent box is — so it never
     * needs its own country lookup; `syncConsentForCountry` drives the
     * show/hide. Authoring the field is optional, so the element may
     * not exist at all — every helper early-outs when it doesn't.
     */
    getDisclaimer() {
        return this.element.querySelector(`.${COMPONENT}__disclaimer`);
    }

    showDisclaimer() {
        const el = this.getDisclaimer();
        if (el) el.hidden = false;
    }

    hideDisclaimer() {
        const el = this.getDisclaimer();
        if (el) el.hidden = true;
    }

    /* ───── Filled-state class for floating labels ───── */

    /**
     * Text inputs use `:placeholder-shown` to drive the floating label,
     * but `<select>` has no placeholder, so we toggle a wrapper class
     * instead. Run on init for any prefilled values, then on every
     * `change` event.
     */
    applyInitialFilledState() {
        this.dom.fields.forEach((f) => this.syncFilledState(f));
    }

    syncFilledState(field) {
        if (!field) return;
        // Checkboxes don't have a floating label — skip the class toggle
        // so a checked CASL/GDPR box doesn't paint the same blue
        // "filled" border as a populated text input.
        if (field.type === 'checkbox') return;
        const wrapper = field.closest(`.${COMPONENT}__field`);
        if (!wrapper) return;
        const filled = !!field.value && String(field.value).trim() !== '';
        wrapper.classList.toggle(`${COMPONENT}__field--filled`, filled);
    }

    /* ───── Event wiring ───── */

    addEventListeners() {
        if (this.dom.form) {
            this.dom.form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleSubmit();
            });
        }

        this.dom.fields.forEach((field) => {
            field.addEventListener('input', () => {
                this.clearFieldError(field);
                this.syncFilledState(field);
            });
            field.addEventListener('change', () => {
                this.clearFieldError(field);
                this.syncFilledState(field);
            });
            field.addEventListener('blur', () => this.validateField(field));
        });

        const countrySelect = this.getCountrySelect();
        if (countrySelect) {
            countrySelect.addEventListener('change', () => this.syncStateForCountry());
        }
    }

    /* ───── Public API ───── */

    /**
     * Hand the calculator controller (and the surrounding <dialog>) over.
     * Called by the Submit Button right before opening the dialog.
     */
    setController(controller, hostDialog) {
        this.controller = controller || null;
        this.hostDialog = hostDialog || this.element.closest('dialog') || null;
        this.clearAllErrors();

        /* If the calculator was prefilled from a share link, the visitor
           already saw the results once — they're not the original lead.
           Disable submit so we don't double-post to Eloqua. */
        if (controller?.isPrefilled) {
            if (this.dom.submit) this.dom.submit.disabled = true;
        }

        /* Intentionally no auto-focus on the first form field — that
           was distracting (the visitor's eye gets yanked to the first
           input the moment the dialog appears). `<dialog>.showModal()`
           still parks default focus inside the dialog (typically the
           close icon), so keyboard / screen-reader users keep their
           focus trap; sighted visitors just see the modal open. */
    }

    /* ───── Validation ───── */

    /**
     * Validate one field. Required + (if `data-elq-type="email"`) email
     * shape, plus must-be-checked enforcement for `data-elq-type="checkbox"`
     * (CASL / GDPR). Hidden / disabled fields are treated as not required —
     * that's how a CASL box for a non-Canadian visitor or a State select
     * for a no-sub-region country stays out of the way.
     */
    validateField(field) {
        if (!field) return true;
        const wrapper = field.closest(`.${COMPONENT}__field`);
        if (!wrapper || wrapper.hidden || field.disabled) {
            this.clearFieldError(field);
            return true;
        }

        const required = field.getAttribute('data-elq-required') === 'true';
        const type = field.getAttribute('data-elq-type') || 'text';
        const errorEl = wrapper.querySelector(`.${COMPONENT}__error`);

        let error = '';
        if (type === 'checkbox') {
            if (required && !field.checked) {
                error = this.config.errorConsent;
            }
        } else {
            const value = (field.value || '').trim();
            if (required && !value) {
                error = this.config.errorRequired;
            } else if (type === 'email' && value && !EMAIL_RE.test(value)) {
                error = this.config.errorEmail;
            }
        }

        if (error) {
            wrapper.classList.add(`${COMPONENT}__field--invalid`);
            if (errorEl) {
                /* Surface the inline message in the `role="alert"` live
                   region by removing the `[hidden]` attribute, and flag
                   the field itself so screen readers announce the
                   failure when the user re-focuses it. */
                errorEl.textContent = error;
                errorEl.removeAttribute('hidden');
            }
            field.setAttribute('aria-invalid', 'true');
            return false;
        }

        this.clearFieldError(field);
        return true;
    }

    clearFieldError(field) {
        if (!field) return;
        const wrapper = field.closest(`.${COMPONENT}__field`);
        const errorEl = wrapper && wrapper.querySelector(`.${COMPONENT}__error`);
        if (wrapper) wrapper.classList.remove(`${COMPONENT}__field--invalid`);
        if (errorEl) {
            errorEl.textContent = '';
            errorEl.setAttribute('hidden', '');
        }
        field.setAttribute('aria-invalid', 'false');
    }

    clearAllErrors() {
        this.dom.fields.forEach((f) => this.clearFieldError(f));
        if (this.dom.formError) {
            this.dom.formError.hidden = true;
            this.dom.formError.textContent = '';
        }
    }

    showFormError(message) {
        if (!this.dom.formError) return;
        this.dom.formError.textContent = message;
        this.dom.formError.hidden = false;
    }

    validateAll() {
        let firstInvalid = null;
        this.dom.fields.forEach((f) => {
            const ok = this.validateField(f);
            if (!ok && !firstInvalid) firstInvalid = f;
        });
        if (firstInvalid) firstInvalid.focus();
        return !firstInvalid;
    }

    /* ───── Submit ───── */

    handleSubmit() {
        if (this.controller?.isPrefilled) return;
        if (!this.validateAll()) return;

        if (!this.config.elqEndpoint) {
            // eslint-disable-next-line no-console
            console.warn('Calculator Eloqua Modal: missing Eloqua Endpoint.');
            this.showFormError(this.config.errorNotConfigured);
            return;
        }

        this.setSubmitting(true);

        // Fire-and-forget: dispatch calculator analytics to futprint in
        // parallel with the Eloqua POST. Wrapped so any error here can never
        // affect the user-facing flow.
        try {
            this.submitToFutprintCalculatorEndpoint();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Calculator Eloqua Modal: futprint dispatch failed', err);
        }

        this.postToEloqua()
            .then(() => this.handleSuccess())
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error('Calculator Eloqua Modal: submission failed', err);
                this.showFormError(this.config.errorSubmission);
                this.setSubmitting(false);
            });
    }

    setSubmitting(isSubmitting) {
        this.element.classList.toggle(`${COMPONENT}--submitting`, isSubmitting);
        if (this.dom.submit) this.dom.submit.disabled = isSubmitting;
    }

    /**
     * Build the urlencoded body the Eloqua endpoint expects.
     *
     * Order matters slightly because URLSearchParams keeps duplicates: we
     * append form fields first (so author-defined hidden inputs win), then
     * per-input/slider Eloqua mappings, then the share link, then UTMs.
     * Eloqua takes the LAST value when keys repeat, so per-calculator
     * mappings can intentionally override an empty author-set hidden of
     * the same name.
     */
    buildPostBody() {
        const body = new URLSearchParams();

        new FormData(this.dom.form).forEach((value, key) => {
            body.append(key, value);
        });

        this.appendCalculatorAnswers(body);
        this.appendResultsUrl(body);
        this.appendUtmParams(body);

        return body;
    }

    /**
     * Walk every calculator input/slider that has `data-elq-field` set on
     * its wrapper and append one entry per field. Value is either the raw
     * answer or `Question - Answer` based on the include-question flag.
     *
     * Reads the structured row data from `controller.buildResults()` so
     * the formatting (locale-aware numbers, prefix/suffix joining) stays
     * in one place — see container.js `buildResultRow`.
     */
    appendCalculatorAnswers(body) {
        if (!this.controller || typeof this.controller.buildResults !== 'function') return;

        const rows = this.controller.buildResults();
        rows.forEach((row) => {
            if (!row.eloquaFieldName) return;
            const out = row.includeQuestion
                ? `${row.label} - ${row.value}`
                : row.value;
            body.append(row.eloquaFieldName, out);
        });
    }

    /**
     * Append the shareable results URL — `<page-url>#<hash>` — under
     * the authored Eloqua column name. The hash carries both the
     * encoded form values AND a `cid=` prefix when the parent
     * Calculator Container has a Container ID, so a multi-calculator
     * page prefills only the calculator the visitor actually used.
     * Skipped entirely if either the column name or the payload is
     * missing.
     */
    appendResultsUrl(body) {
        if (!this.config.resultsUrlField) return;
        if (!this.controller || typeof this.controller.getShareUrlHash !== 'function') return;

        const hash = this.controller.getShareUrlHash();
        if (!hash) return;

        body.append(
            this.config.resultsUrlField,
            `${window.location.origin}${window.location.pathname}#${hash}`,
        );
    }

    appendUtmParams(body) {
        const urlParams = new URLSearchParams(window.location.search);
        UTM_KEYS.forEach((utm) => {
            const v = urlParams.get(utm);
            if (v) body.append(utm, v);
        });
    }

    postToEloqua() {
        return fetch(this.config.elqEndpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
            mode: 'no-cors',
            credentials: 'omit',
            body: this.buildPostBody(),
        });
    }

    /**
     * POST a JSON-encoded calculator analytics payload to the futprint
     * dashboard ingestion endpoint. Independent of the Eloqua submission;
     * failures are swallowed and never surface to the user. Same-origin —
     * futprint and rockwell-aem run on the same AEM instance.
     */
    submitToFutprintCalculatorEndpoint() {
        if (!this.controller || typeof this.controller.buildCalculatorPayload !== 'function') return;

        const guidInput = this.dom.form && this.dom.form.querySelector('input[name="elqCustomerGUID"]');
        const payload = this.controller.buildCalculatorPayload({
            eloquaID: guidInput ? guidInput.value : '',
        });

        const profile = this.buildFutprintProfile();
        if (Object.keys(profile).length > 0) {
            payload.profile = profile;
        }

        fetch(FUTPRINT_CALCULATOR_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
        }).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn('Calculator: futprint analytics POST failed', err);
        });
    }

    /**
     * Read the four form-field values that map to the Futprint dashboard's
     * canonical demographic keys (`country`, `industry`, `job_function`,
     * `job_level`). The Eloqua Field Name to read is configurable via the
     * Futprint Key Mapping tab; default lookup is the canonical key itself.
     *
     * Returns the dropdown's `value` (Q4 from the design discussion), not
     * the option's display label. Empty values are omitted so the servlet
     * doesn't persist blank props.
     */
    buildFutprintProfile() {
        const profile = {};
        if (!this.dom.form || !this.config.futprintKeys) return profile;
        Object.entries(this.config.futprintKeys).forEach(([canonicalKey, eloquaName]) => {
            if (!eloquaName) return;
            const field = this.dom.form.elements[eloquaName];
            if (!field) return;
            const value = (field.value || '').trim();
            if (value) profile[canonicalKey] = value;
        });
        return profile;
    }

    handleSuccess() {
        const dialog = this.hostDialog || this.element.closest('dialog');
        if (dialog && typeof dialog.close === 'function') {
            dialog.close();
        }

        this.setSubmitting(false);
        if (this.dom.form) {
            this.dom.form.reset();
            this.applyInitialFilledState();
            if (!this.isEditMode) {
                this.hideStateField();
                this.hideConsent('casl');
                this.hideConsent('gdpr');
                this.hideDisclaimer();
            }
        }

        if (this.controller && typeof this.controller.showResultsView === 'function') {
            this.controller.showResultsView();
        }
    }
}

registerComponent(`.${COMPONENT}`, CalculatorEloquaModal);
